import type {
  ThreatMapAvailableButNotSelected,
  ThreatMapProvenance,
  ThreatMapUnknownReason,
} from '@/types/map'
import {
  formatAvailableButNotSelected,
  projectionReasonLabel,
  provenanceFieldLabel,
  provenanceSourceLabel,
} from './map-display-utils'

export interface ReasonChipsProps {
  reasons?: ThreatMapUnknownReason[]
  max?: number
  label?: string
}

export function ReasonChips({ reasons = [], max = 3, label = 'Projection reasons' }: ReasonChipsProps) {
  const unique = [...new Set(reasons)]
  if (unique.length === 0) return null

  const visible = unique.slice(0, max)
  const overflow = unique.length - visible.length

  return (
    <div aria-label={label} className="flex flex-wrap gap-1" data-testid="map-reason-chips">
      {visible.map((reason) => (
        <span
          key={reason}
          data-testid="map-reason-chip"
          className="inline-flex rounded border border-[color:var(--border-subtle)] bg-[color:var(--surface-tertiary)] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]"
          title={reason}
        >
          {projectionReasonLabel(reason)}
        </span>
      ))}
      {overflow > 0 && (
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
          +{overflow} more
        </span>
      )}
    </div>
  )
}

export interface ProvenanceMiniLineProps {
  provenance?: ThreatMapProvenance[]
}

export function ProvenanceMiniLine({ provenance = [] }: ProvenanceMiniLineProps) {
  const selected = provenance.find((entry) => entry.selected) ?? provenance[0]
  if (!selected) return null

  return (
    <span data-testid="map-provenance-line">
      {provenanceSourceLabel(selected.source)} · {selected.reason ?? provenanceFieldLabel(selected.field)}
      {selected.confidence ? ` · conf ${selected.confidence}` : ''}
    </span>
  )
}

export interface ProvenanceDetailsProps {
  provenance?: ThreatMapProvenance[]
  availableButNotSelected?: ThreatMapAvailableButNotSelected[]
  evidenceIds?: Array<{ label: string; value?: string | null }>
}

export function ProvenanceDetails({
  provenance = [],
  availableButNotSelected = [],
  evidenceIds = [],
}: ProvenanceDetailsProps) {
  const visibleEvidence = evidenceIds.filter((entry) => entry.value)
  if (provenance.length === 0 && availableButNotSelected.length === 0 && visibleEvidence.length === 0) {
    return null
  }

  return (
    <div data-testid="map-provenance-details" className="space-y-2">
      {provenance.length > 0 && (
        <ul className="space-y-1">
          {provenance.map((entry, index) => (
            <li
              key={`${entry.source}:${entry.field}:${index}`}
              data-testid="map-provenance-entry"
              className="rounded border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] px-2 py-1.5"
            >
              <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em]">
                <span className="text-[color:var(--text-secondary)]">
                  {provenanceSourceLabel(entry.source)}
                </span>
                <span className="text-[color:var(--text-tertiary)]">·</span>
                <span className="text-[color:var(--text-tertiary)]">
                  {entry.selected ? 'selected' : 'not selected'}
                </span>
                {entry.confidence && (
                  <span className="text-[color:var(--text-tertiary)]">conf {entry.confidence}</span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-[color:var(--text-secondary)]">
                {entry.reason ?? provenanceFieldLabel(entry.field)}
              </p>
              <p className="mt-0.5 font-mono text-[9px] text-[color:var(--text-tertiary)]">
                {provenanceFieldLabel(entry.field)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {availableButNotSelected.length > 0 && (
        <div className="rounded border border-dashed border-[color:var(--border-subtle)] px-2 py-1.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
            Available but not selected
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-[color:var(--text-secondary)]">
            {availableButNotSelected.map((entry) => (
              <li key={entry.domain} data-testid="map-available-not-selected">
                {formatAvailableButNotSelected(entry)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {visibleEvidence.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-1 font-mono text-[10px] text-[color:var(--text-tertiary)]">
          {visibleEvidence.map((entry) => (
            <div key={entry.label} className="contents" data-testid="map-evidence-id">
              <dt className="uppercase tracking-[0.12em]">{entry.label}</dt>
              <dd className="min-w-0 truncate text-[color:var(--text-secondary)]">{entry.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
